/**
 * GST-PAN Cross Validation Utility
 *
 * GST Number Format: 15 characters
 * - Characters 1-2: State Code (e.g., "07" for Delhi)
 * - Characters 3-12: PAN Number (10 characters)
 * - Character 13: Entity number (1-9, A-Z)
 * - Character 14: Always "Z"
 * - Character 15: Check digit (0-9, A-Z)
 *
 * Example: 07AAXXX1234A1Z5
 * - "07" = Delhi State Code
 * - "AAXXX1234A" = PAN Number
 * - "1" = Entity number
 * - "Z" = Fixed character
 * - "5" = Check digit
 */

/**
 * Extract PAN number from GST number
 * @param {string} gstNumber - 15 character GST number
 * @returns {string} - 10 character PAN number extracted from GST
 */
export const extractPANFromGST = (gstNumber) => {
  if (!gstNumber || typeof gstNumber !== "string") return "";
  const trimmed = gstNumber.trim().toUpperCase();
  if (trimmed.length !== 15) return "";
  // Extract characters 3-12 (index 2-11) which is the PAN
  return trimmed.substring(2, 12);
};

/**
 * Validate if PAN number matches the PAN embedded in GST number
 * @param {string} gstNumber - 15 character GST number
 * @param {string} panNumber - 10 character PAN number
 * @returns {object} - { isValid: boolean, message: string, extractedPAN: string }
 */
export const validateGSTPANMatch = (gstNumber, panNumber) => {
  // Normalize inputs
  const gst = (gstNumber || "").trim().toUpperCase();
  const pan = (panNumber || "").trim().toUpperCase();

  // If either is empty, skip validation
  if (!gst || !pan) {
    return {
      isValid: true,
      message: "",
      extractedPAN: "",
    };
  }

  // Validate GST format first
  if (gst.length !== 15) {
    return {
      isValid: false,
      message: "GST number must be exactly 15 characters",
      extractedPAN: "",
    };
  }

  // Validate PAN format first
  if (pan.length !== 10) {
    return {
      isValid: false,
      message: "PAN number must be exactly 10 characters",
      extractedPAN: "",
    };
  }

  // Extract PAN from GST (characters 3-12)
  const extractedPAN = extractPANFromGST(gst);

  // Compare PAN numbers
  if (extractedPAN !== pan) {
    return {
      isValid: false,
      message: `GST-PAN mismatch: PAN in GST is "${extractedPAN}" but entered PAN is "${pan}"`,
      extractedPAN,
    };
  }

  return {
    isValid: true,
    message: "PAN number matches with GST",
    extractedPAN,
  };
};

/**
 * Validate multiple GST numbers against a single PAN
 * Used when entity has multiple GST registrations
 * @param {Array} gstEntries - Array of GST entry objects with gstNumber field
 * @param {string} panNumber - 10 character PAN number
 * @returns {object} - { isValid: boolean, errors: Array<{index, message}> }
 */
export const validateMultipleGSTWithPAN = (gstEntries, panNumber) => {
  const errors = [];
  const pan = (panNumber || "").trim().toUpperCase();

  if (!pan) {
    return { isValid: true, errors: [] };
  }

  gstEntries.forEach((entry, index) => {
    const gstNumber = (entry.gstNumber || "").trim().toUpperCase();
    if (!gstNumber) return; // Skip empty GST entries

    const result = validateGSTPANMatch(gstNumber, pan);
    if (!result.isValid) {
      errors.push({
        index,
        gstNumber,
        message: result.message,
        extractedPAN: result.extractedPAN,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get state code from GST number
 * @param {string} gstNumber - 15 character GST number
 * @returns {string} - 2 digit state code
 */
export const getStateCodeFromGST = (gstNumber) => {
  if (!gstNumber || typeof gstNumber !== "string") return "";
  const trimmed = gstNumber.trim();
  if (trimmed.length < 2) return "";
  return trimmed.substring(0, 2);
};

/**
 * Format GST number for display with PAN highlighted
 * @param {string} gstNumber - 15 character GST number
 * @returns {object} - { stateCode, pan, suffix, formatted }
 */
export const formatGSTWithPAN = (gstNumber) => {
  if (!gstNumber || typeof gstNumber !== "string") {
    return { stateCode: "", pan: "", suffix: "", formatted: "" };
  }

  const trimmed = gstNumber.trim().toUpperCase();
  if (trimmed.length !== 15) {
    return { stateCode: "", pan: "", suffix: "", formatted: trimmed };
  }

  const stateCode = trimmed.substring(0, 2);
  const pan = trimmed.substring(2, 12);
  const suffix = trimmed.substring(12, 15);

  return {
    stateCode,
    pan,
    suffix,
    formatted: `${stateCode}-${pan}-${suffix}`,
  };
};
